import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import ConfirmationModal from "../components/ConfirmationModal";

// Création du contexte
const CartContext = createContext();

// Styles personnalisés pour les toasts
const toastStyle = {
  success: {
    style: {
      background: "#fff",
      color: "#543ba4",
      borderLeft: "4px solid #543ba4",
    },
    progressStyle: {
      background: "#543ba4",
    },
  },
  info: {
    style: {
      background: "#fff",
      color: "#666666",
      borderLeft: "4px solid #666666",
    },
    progressStyle: {
      background: "#666666",
    },
  },
  warning: {
    style: {
      background: "#fff",
      color: "#f89638",
      borderLeft: "4px solid #f89638",
    },
    progressStyle: {
      background: "#f89638",
    },
  },
  error: {
    style: {
      background: "#fff",
      color: "#dc3545",
      borderLeft: "4px solid #dc3545",
    },
    progressStyle: {
      background: "#dc3545",
    },
  },
};

// Items avec images réelles pour simuler les produits
const DUMMY_PRODUCTS = [
  {
    id: 1,
    name: "Box Essentielle",
    description:
      "Notre box de base avec tous les produits essentiels pour votre bien-être quotidien.",
    price: 29.99,
    image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=300&fit=crop",
    category: "Mensuelle",
    rating: 4.5,
    reviewCount: 28,
    inStock: true,
    isNew: false,
    hasSubscriptionOption: true,
  },
  {
    id: 2,
    name: "Box Premium",
    description:
      "Notre box premium avec des produits exclusifs et haut de gamme pour les connaisseurs.",
    price: 49.99,
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
    category: "Mensuelle",
    rating: 4.8,
    reviewCount: 36,
    inStock: true,
    isNew: true,
    hasSubscriptionOption: true,
  },
  {
    id: 3,
    name: "Box Découverte",
    description:
      "Une sélection surprise de nouveaux produits à découvrir tous les mois.",
    price: 39.99,
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=300&fit=crop",
    category: "Mensuelle",
    rating: 4.3,
    reviewCount: 19,
    inStock: true,
    isNew: false,
    hasSubscriptionOption: true,
  },
  {
    id: 4,
    name: "Box Édition Limitée Été",
    description:
      "Une édition spéciale et limitée avec des produits saisonniers pour l'été.",
    price: 59.99,
    image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop",
    category: "Édition limitée",
    rating: 4.9,
    reviewCount: 14,
    inStock: true,
    isNew: true,
    hasSubscriptionOption: false,
  },
  {
    id: 5,
    name: "Box Cadeau",
    description:
      "Le cadeau parfait pour faire plaisir à vos proches avec une sélection de produits variés.",
    price: 24.99,
    image: "https://images.unsplash.com/photo-1606728035253-49e8a23146de?w=400&h=300&fit=crop",
    category: "Cadeau",
    rating: 4.2,
    reviewCount: 22,
    inStock: true,
    isNew: false,
    hasSubscriptionOption: false,
  },
  {
    id: 6,
    name: "Box Wellness",
    description:
      "Prenez soin de vous avec notre sélection bien-être dédiée à votre santé.",
    price: 45.99,
    image: "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&h=300&fit=crop",
    category: "Premium",
    rating: 4.6,
    reviewCount: 31,
    inStock: true,
    isNew: false,
    hasSubscriptionOption: true,
  },
  {
    id: 7,
    name: "Box Collector",
    description:
      "Une édition collector avec des produits rares et exclusifs pour les collectionneurs.",
    price: 79.99,
    image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop",
    category: "Édition limitée",
    rating: 4.9,
    reviewCount: 18,
    inStock: true,
    isNew: true,
    hasSubscriptionOption: false,
  },
  {
    id: 8,
    name: "Box Famille",
    description:
      "Des moments de partage en famille avec des produits pour tous les âges.",
    price: 34.99,
    image: "https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=400&h=300&fit=crop",
    category: "Mensuelle",
    rating: 4.3,
    reviewCount: 25,
    inStock: true,
    isNew: false,
    hasSubscriptionOption: true,
  },
];

// Actions pour le reducer
const CART_ACTIONS = {
  ADD_ITEM: "ADD_ITEM",
  REMOVE_ITEM: "REMOVE_ITEM",
  UPDATE_QUANTITY: "UPDATE_QUANTITY",
  CLEAR_CART: "CLEAR_CART",
  SET_SUBSCRIPTION: "SET_SUBSCRIPTION",
};

// État initial du panier
const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  subscriptionItems: [], // Tableau pour garder trace des items en abonnement
};

// Reducer pour gérer les changements d'état du panier
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.id === action.payload.product.id &&
          item.isSubscription === action.payload.isSubscription
      );

      let updatedItems;

      if (existingItemIndex !== -1) {
        // Si l'item existe déjà, augmenter la quantité
        updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + 1,
        };

        const subscriptionText = action.payload.isSubscription
          ? " (abonnement)"
          : "";
        toast.info(
          `Quantité de ${action.payload.product.name}${subscriptionText} augmentée`,
          toastStyle.info
        );
      } else {
        // Sinon, ajouter le nouvel item
        const newItem = {
          ...action.payload.product,
          quantity: 1,
          isSubscription: action.payload.isSubscription || false,
        };
        updatedItems = [...state.items, newItem];

        const subscriptionText = action.payload.isSubscription
          ? " (abonnement)"
          : "";
        toast.success(
          `${action.payload.product.name}${subscriptionText} ajouté au panier`,
          toastStyle.success
        );
      }

      // Calculer le nouveau total
      const totalItems = updatedItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = updatedItems.reduce((sum, item) => {
        let itemPrice = item.price;
        // Appliquer une réduction de 15% pour les items en abonnement
        if (item.isSubscription) {
          itemPrice = itemPrice * 0.85;
        }
        return sum + itemPrice * item.quantity;
      }, 0);

      // Mettre à jour la liste des items en abonnement
      const subscriptionItems = updatedItems
        .filter((item) => item.isSubscription)
        .map((item) => item.id);

      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice,
        subscriptionItems,
      };
    }

    case CART_ACTIONS.REMOVE_ITEM: {
      const updatedItems = state.items.filter(
        (item) =>
          !(
            item.id === action.payload.id &&
            item.isSubscription === action.payload.isSubscription
          )
      );

      const totalItems = updatedItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = updatedItems.reduce((sum, item) => {
        let itemPrice = item.price;
        if (item.isSubscription) {
          itemPrice = itemPrice * 0.85;
        }
        return sum + itemPrice * item.quantity;
      }, 0);

      // Mettre à jour la liste des items en abonnement
      const subscriptionItems = updatedItems
        .filter((item) => item.isSubscription)
        .map((item) => item.id);

      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice,
        subscriptionItems,
      };
    }

    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { id, quantity, isSubscription = false } = action.payload;

      // Si la quantité est 0, on supprime l'item
      if (quantity <= 0) {
        return cartReducer(state, {
          type: CART_ACTIONS.REMOVE_ITEM,
          payload: { id, isSubscription },
        });
      }

      const updatedItems = state.items.map((item) =>
        item.id === id && item.isSubscription === isSubscription
          ? { ...item, quantity }
          : item
      );

      const totalItems = updatedItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = updatedItems.reduce((sum, item) => {
        let itemPrice = item.price;
        if (item.isSubscription) {
          itemPrice = itemPrice * 0.85;
        }
        return sum + itemPrice * item.quantity;
      }, 0);

      // Mettre à jour la liste des items en abonnement
      const subscriptionItems = updatedItems
        .filter((item) => item.isSubscription)
        .map((item) => item.id);

      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice,
        subscriptionItems,
      };
    }

    case CART_ACTIONS.SET_SUBSCRIPTION: {
      const { id, isSubscription } = action.payload;

      // Trouver l'item actuel
      const currentItemIndex = state.items.findIndex(
        (item) => item.id === id && item.isSubscription !== isSubscription
      );

      if (currentItemIndex === -1) {
        // L'item n'existe pas dans le mode actuel, ne rien faire
        return state;
      }

      const currentItem = state.items[currentItemIndex];

      // Vérifier si l'item existe déjà dans le nouveau mode
      const newModeItemIndex = state.items.findIndex(
        (item) => item.id === id && item.isSubscription === isSubscription
      );

      let updatedItems;

      if (newModeItemIndex !== -1) {
        // Si l'item existe déjà dans le nouveau mode, ajouter la quantité
        updatedItems = [...state.items];
        updatedItems[newModeItemIndex] = {
          ...updatedItems[newModeItemIndex],
          quantity:
            updatedItems[newModeItemIndex].quantity + currentItem.quantity,
        };

        // Supprimer l'ancien item
        updatedItems.splice(currentItemIndex, 1);
      } else {
        // Sinon, mettre à jour l'item existant
        updatedItems = [...state.items];
        updatedItems[currentItemIndex] = {
          ...currentItem,
          isSubscription,
        };
      }

      const totalItems = updatedItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = updatedItems.reduce((sum, item) => {
        let itemPrice = item.price;
        if (item.isSubscription) {
          itemPrice = itemPrice * 0.85;
        }
        return sum + itemPrice * item.quantity;
      }, 0);

      // Mettre à jour la liste des items en abonnement
      const subscriptionItems = updatedItems
        .filter((item) => item.isSubscription)
        .map((item) => item.id);

      const mode = isSubscription ? "abonnement" : "achat unique";
      toast.info(`${currentItem.name} passé en mode ${mode}`, toastStyle.info);

      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice,
        subscriptionItems,
      };
    }

    case CART_ACTIONS.CLEAR_CART:
      return initialState;

    default:
      return state;
  }
};

// Provider du contexte
export const CartProvider = ({ children }) => {
  // État pour le modal de confirmation
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    itemId: null,
    itemName: "",
    isSubscription: false,
    action: null,
    message: "",
  });

  // Récupérer le panier du localStorage au chargement
  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem("cart");
      return savedCart ? JSON.parse(savedCart) : initialState;
    } catch (error) {
      console.error("Erreur lors du chargement du panier:", error);
      return initialState;
    }
  };

  const [cartState, dispatch] = useReducer(
    cartReducer,
    initialState,
    loadCartFromStorage
  );

  // Sauvegarder le panier dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartState));
  }, [cartState]);

  // Actions du panier
  const addItem = (product, isSubscription = false) => {
    dispatch({
      type: CART_ACTIONS.ADD_ITEM,
      payload: { product, isSubscription },
    });
  };

  // Fonction pour montrer le modal de confirmation de suppression
  const confirmRemoveItem = (
    productId,
    productName,
    isSubscription = false
  ) => {
    const subscriptionText = isSubscription ? " (abonnement)" : "";
    setConfirmModal({
      show: true,
      itemId: productId,
      itemName: productName,
      isSubscription,
      action: "remove",
      message: `Êtes-vous sûr de vouloir retirer "${productName}${subscriptionText}" de votre panier ?`,
    });
  };

  // Fonction pour montrer le modal de confirmation de vidage du panier
  const confirmClearCart = () => {
    setConfirmModal({
      show: true,
      itemId: null,
      itemName: "",
      isSubscription: false,
      action: "clear",
      message:
        "Êtes-vous sûr de vouloir vider votre panier ? Cette action est irréversible.",
    });
  };

  // Fonction pour fermer le modal
  const closeConfirmModal = () => {
    setConfirmModal({
      ...confirmModal,
      show: false,
    });
  };

  // Fonction pour confirmer l'action dans le modal
  const handleConfirmAction = () => {
    if (confirmModal.action === "remove") {
      dispatch({
        type: CART_ACTIONS.REMOVE_ITEM,
        payload: {
          id: confirmModal.itemId,
          isSubscription: confirmModal.isSubscription,
        },
      });

      const subscriptionText = confirmModal.isSubscription
        ? " (abonnement)"
        : "";
      toast.info(
        `${confirmModal.itemName}${subscriptionText} retiré du panier`,
        toastStyle.info
      );
    } else if (confirmModal.action === "clear") {
      dispatch({ type: CART_ACTIONS.CLEAR_CART });
      toast.info("Panier vidé", toastStyle.info);
    } else if (confirmModal.action === "switch-to-subscription") {
      dispatch({
        type: CART_ACTIONS.SET_SUBSCRIPTION,
        payload: { id: confirmModal.itemId, isSubscription: true },
      });
    } else if (confirmModal.action === "switch-to-single") {
      dispatch({
        type: CART_ACTIONS.SET_SUBSCRIPTION,
        payload: { id: confirmModal.itemId, isSubscription: false },
      });
    }
    closeConfirmModal();
  };

  // Fonction pour mettre à jour la quantité
  const updateQuantity = (productId, quantity, isSubscription = false) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { id: productId, quantity, isSubscription },
    });
  };

  // Fonction pour changer un produit entre abonnement et achat unique
  const toggleSubscription = (
    productId,
    productName,
    currentSubscriptionStatus
  ) => {
    const newStatus = !currentSubscriptionStatus;
    const actionType = newStatus
      ? "switch-to-subscription"
      : "switch-to-single";
    const message = newStatus
      ? `Voulez-vous passer "${productName}" en mode abonnement ? Vous bénéficierez d'une réduction de 15%.`
      : `Voulez-vous passer "${productName}" en mode achat unique ? Vous ne bénéficierez plus de la réduction de 15%.`;

    setConfirmModal({
      show: true,
      itemId: productId,
      itemName: productName,
      isSubscription: currentSubscriptionStatus,
      action: actionType,
      message,
    });
  };

  // Fonction directe de suppression (sans confirmation, pour compatibilité)
  const removeItem = (productId, isSubscription = false) => {
    dispatch({
      type: CART_ACTIONS.REMOVE_ITEM,
      payload: { id: productId, isSubscription },
    });
  };

  // Fonction directe de vidage (sans confirmation, pour compatibilité)
  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  // Nouvelle fonction pour vider le panier directement sans confirmation
  const clearCartDirectly = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  // Fonction pour récupérer un produit par son ID
  const getProductById = (productId) => {
    return DUMMY_PRODUCTS.find((product) => product.id === productId);
  };

  // Valeur du contexte à exposer
  const value = {
    items: cartState.items,
    totalItems: cartState.totalItems,
    totalPrice: cartState.totalPrice,
    subscriptionItems: cartState.subscriptionItems,
    addItem,
    removeItem,
    confirmRemoveItem,
    updateQuantity,
    clearCart,
    confirmClearCart,
    clearCartDirectly, // Nouvelle fonction ajoutée
    toggleSubscription,
    getProductById,
    products: DUMMY_PRODUCTS, // Exposer la liste des produits
  };

  return (
    <>
      <CartContext.Provider value={value}>{children}</CartContext.Provider>

      {/* Modal de confirmation */}
      <ConfirmationModal
        show={confirmModal.show}
        title={
          confirmModal.action === "remove"
            ? "Retirer du panier"
            : confirmModal.action === "clear"
            ? "Vider le panier"
            : confirmModal.action === "switch-to-subscription"
            ? "Passer en abonnement"
            : "Passer en achat unique"
        }
        message={confirmModal.message}
        confirmText={
          confirmModal.action === "remove"
            ? "Retirer"
            : confirmModal.action === "clear"
            ? "Vider"
            : "Confirmer"
        }
        cancelText="Annuler"
        confirmButtonClass={
          confirmModal.action === "remove" || confirmModal.action === "clear"
            ? "btn-danger"
            : "btn-primary"
        }
        onConfirm={handleConfirmAction}
        onCancel={closeConfirmModal}
        icon={
          confirmModal.action === "remove"
            ? "trash"
            : confirmModal.action === "clear"
            ? "cart-x"
            : confirmModal.action === "switch-to-subscription"
            ? "calendar-check"
            : "bag"
        }
      />
    </>
  );
};

// Hook personnalisé pour utiliser le contexte du panier
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error(
      "useCart doit être utilisé à l'intérieur d'un CartProvider"
    );
  }
  return context;
};

export default CartContext;